/* New fan flow — compact layout. No rail: one toolbar (count, Expand/Collapse
   all, Load example, Add step) over a single card that reads as a table:
   # / Step / Example message / AI coaching / reorder + delete. Collapsed rows
   preview the example message as a purple chat bubble; clicking a row opens
   its Name, Example message and AI coaching fields. Load example replaces the
   steps and offers Undo. Every change saves on its own — there is no Save bar. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account } from '../../../../api/client';
import { useAutosave } from '../../../../hooks/useAutosave';
import { Btn, Icon, PageFrame, SaveStatus, Stack, Warn } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';
import { isBlank, payloadOf, stepId, stepsOf, type Step } from './model';

const COLS = '24px minmax(110px,170px) minmax(0,1.2fr) minmax(0,1fr) 84px';
const EXAMPLE_STEPS = [1, 2, 3, 4];

const labelStyle = { font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, display: 'flex', alignItems: 'center', gap: 5 } as const;

export function NewFanFlowPage({ account, onAccountUpdate }: { account: Account; onAccountUpdate: (a: Account) => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('chatAssistant');
  const id = account.account_id;

  // The page is keyed by account, so the account it mounts with is the saved state.
  const [steps, setSteps] = useState<Step[]>(() => stepsOf(account.start_with_new_fan_setup));
  const [undo, setUndo] = useState<Step[] | null>(null);
  const auto = useAutosave({
    value: payloadOf(steps),
    delay: 600,
    save: async (v) => { onAccountUpdate(await api.updateAccount(id, { start_with_new_fan_setup: v })); },
  });

  const n = steps.length;
  const anyOpen = steps.some((s) => s.open);
  const edit = (k: number, p: Partial<Step>) => setSteps((a) => a.map((s, j) => (j === k ? { ...s, ...p } : s)));
  const move = (k: number, dir: -1 | 1) => setSteps((a) => {
    const j = k + dir;
    if (j < 0 || j >= a.length) return a;
    const next = a.slice();
    const [x] = next.splice(k, 1);
    next.splice(j, 0, x!);
    return next;
  });
  const add = () => setSteps((a) => [...a.map((s) => ({ ...s, open: false })), { id: stepId(), name: '', message: '', coaching: '', open: true }]);
  const loadExample = () => {
    setUndo(steps);
    setSteps(EXAMPLE_STEPS.map((i) => ({
      id: stepId(),
      name: tc(`newFanFlow.template.step${i}.heading`),
      message: tc(`newFanFlow.template.step${i}.message`),
      coaching: tc(`newFanFlow.template.step${i}.description`),
      open: false,
    })));
  };

  return (
    <PageFrame headline={t('nf.title')} desc={t('nf.desc')} right={<SaveStatus status={auto.status} error={auto.error} onRetry={auto.retry} idleLabel />}>
      <Stack gap={12}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ font: f(500, 12), color: C.sub, whiteSpace: 'nowrap' }}>{t('nf.count', { count: n })}</span>
          <span style={{ font: f(400, 11.5), color: C.muted }}>{t('nf.inOrder')}</span>
          <span style={{ flex: 1 }} />
          {n > 0 && <Btn kind="text" onClick={() => setSteps((a) => a.map((s) => ({ ...s, open: !anyOpen })))} style={{ color: C.purpleLt }}>{anyOpen ? t('nf.collapseAll') : t('nf.expandAll')}</Btn>}
          <Btn icon="copy" title={t('nf.loadTip')} onClick={loadExample}>{t('nf.loadExample')}</Btn>
          <Btn kind="primary" icon="plus" onClick={add}>{t('nf.addStep')}</Btn>
        </div>

        {undo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'color-mix(in srgb, var(--om-accent) 12%, transparent)', border: `1px solid ${C.tintLine}`, font: f(500, 12), color: C.purplePale }}>
            <Icon name="info" size={13} />
            <span style={{ flex: 1 }}>{t('nf.loaded')}</span>
            <Btn kind="primary" height={24} onClick={() => { setSteps(undo); setUndo(null); }}>{t('nf.undo')}</Btn>
            <button type="button" title={t('close')} onClick={() => setUndo(null)} className="cpt-hover-text" style={{ width: 24, height: 24, border: 0, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="x" size={13} /></button>
          </div>
        )}

        <div className="cpt-card" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="cpt-nf-grid" style={{ ...labelStyle, display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center', height: 30, padding: '0 10px 0 14px' }}>
            <span>#</span>
            <span>{t('nf.colStep')}</span>
            <span className="cpt-nf-prev" title={t('nf.messageTip')} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'help' }}>{t('nf.colMessage')}<Icon name="info" size={11} /></span>
            <span className="cpt-nf-prev" title={t('nf.coachingTip')} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'help' }}>{t('nf.colCoaching')}<Icon name="info" size={11} /></span>
            <span />
          </div>
          {steps.map((s, k) => (
            <StepRow key={s.id} step={s} n={k + 1} first={k === 0} last={k === n - 1} onEdit={(p) => edit(k, p)} onMove={(d) => move(k, d)} onDelete={() => setSteps((a) => a.filter((_, j) => j !== k))} />
          ))}
          {!n && <div style={{ padding: 24, textAlign: 'center', font: f(400, 12), color: C.muted }}>{t('nf.none')}</div>}
        </div>
      </Stack>
    </PageFrame>
  );
}

function StepRow({ step: s, n, first, last, onEdit, onMove, onDelete }: { step: Step; n: number; first: boolean; last: boolean; onEdit: (p: Partial<Step>) => void; onMove: (d: -1 | 1) => void; onDelete: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onEdit({ open: !s.open })}
        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onEdit({ open: !s.open }); } }}
        className="cpt-hover-row cpt-nf-grid"
        style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center', minHeight: 44, padding: '0 10px 0 14px', cursor: 'pointer' }}
      >
        <span style={{ width: 22, height: 22, boxSizing: 'border-box', borderRadius: 99, border: `1.5px solid ${C.purple}`, display: 'flex', alignItems: 'center', justifyContent: 'center', font: f(700, 11), color: C.purplePale }}>{n}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <Icon name="chevron-right" size={12} color={C.muted} style={{ flex: 'none', transform: s.open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
          <span style={{ flex: '1 1 auto', minWidth: 0, font: f(600, 13), color: s.name ? C.text : C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name || t('nf.untitled')}</span>
        </span>
        <span className="cpt-nf-prev" style={{ minWidth: 0, display: 'flex' }}>
          {!s.open && s.message.trim() && (
            <span style={{ maxWidth: '100%', boxSizing: 'border-box', padding: '6px 12px', borderRadius: '14px 14px 14px 4px', background: C.tint16, border: '1px solid color-mix(in srgb, var(--om-accent) 40%, transparent)', font: f(400, 12.5), color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.message}</span>
          )}
        </span>
        <span className="cpt-nf-prev" style={{ font: `italic ${f(400, 12)}`, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.open ? '' : s.coaching || '—'}</span>
        <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <IconBtn icon="chevron-up" title={t('nf.moveUp')} disabled={first} onClick={stop(() => onMove(-1))} />
          <IconBtn icon="chevron-down" title={t('nf.moveDown')} disabled={last} onClick={stop(() => onMove(1))} />
          <IconBtn icon="trash-2" title={t('nf.delete')} onClick={stop(onDelete)} />
        </span>
      </div>
      {s.open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '2px 14px 14px 50px' }} className="cpt-nf-edit">
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>{t('nf.name')}</span>
            <input value={s.name} onChange={(e) => onEdit({ name: e.target.value })} placeholder={t('nf.namePh')} style={{ height: 30, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 7, padding: '0 10px', color: C.text, font: f(500, 12.5), outline: 'none', maxWidth: 320 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}><Icon name="message-square" size={11} />{t('nf.colMessage')}</span>
            <textarea value={s.message} onChange={(e) => onEdit({ message: e.target.value })} placeholder={t('nf.messagePh')} style={{ minHeight: 48, maxWidth: 640, resize: 'vertical', boxSizing: 'border-box', background: C.tint16, border: `1px solid ${C.tintLine45}`, borderRadius: '16px 16px 16px 4px', padding: '10px 14px', color: C.text, font: f(400, 13, 1.5), outline: 'none', width: '100%' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}><Icon name="zap" size={11} />{t('nf.colCoaching')}</span>
            <textarea value={s.coaching} onChange={(e) => onEdit({ coaching: e.target.value })} placeholder={t('nf.coachingPh')} style={{ minHeight: 56, resize: 'vertical', boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 7, padding: '8px 10px', color: C.text, font: f(400, 12.5, 1.5), outline: 'none', width: '100%' }} />
          </label>
          {isBlank(s) && <Warn>{t('nf.blank')}</Warn>}
        </div>
      )}
    </div>
  );
}

function IconBtn({ icon, title, disabled, onClick }: { icon: string; title: string; disabled?: boolean; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button type="button" title={title} disabled={disabled} onClick={onClick} className={disabled ? undefined : 'cpt-hover-row cpt-hover-text'} style={{ width: 26, height: 26, flex: 'none', borderRadius: 6, border: 0, background: 'transparent', color: C.muted, cursor: disabled ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: disabled ? 0.3 : 1 }}>
      <Icon name={icon} size={13} />
    </button>
  );
}
